// The wheel's measurements, in world units, with the wheel lying in the XZ
// plane and Y up. Everything that has to agree about where a pocket, the ball
// track or an avatar chip *is* — the mesh builder, the spin choreography, the
// 2D fallback painter — reads it from here rather than keeping its own copy.

/**
 * The bowl, out from the wheel head.
 *
 * A real wheel's head is a little under three quarters of the width of the
 * bowl it sits in, and the bowl around it is a shallow dish — a long, gentle
 * slope of about twenty-five degrees, with the ball track and its outer wall
 * only at the very edge. Squeezing the same drop into a narrow band, as this
 * did at first, reads as a bucket rather than as a roulette bowl.
 */
export const R_RIM = 1.36;
/** Where the dish gives way to the outer wall the ball rides against. */
export const R_TRACK = 1.28;
/** The pocket ring on the rotating head. */
export const R_POCKET_OUT = 0.96;
export const R_POCKET_IN = 0.5;
export const R_POCKET_MID = (R_POCKET_OUT + R_POCKET_IN) / 2;

/**
 * Floor of a pocket, and the top of the frets between them. A fret stands a
 * little over half a ball's diameter, as on a real wheel: tall enough to
 * catch the ball, low enough that it still sits proud of the wheel rather
 * than being swallowed by it. The top is above the plane of the pocket ring,
 * which is what makes the pockets read as pockets from a shallow angle — a
 * fret sunk level with the ring leaves 22 painted stripes rather than 22
 * compartments with steel walls between them.
 */
export const POCKET_FLOOR_Y = -0.082;
export const FRET_TOP_Y = -0.004;
/**
 * The low wall round the outer edge of the pocket ring. It belongs to the
 * turning head, not to the bowl, and that is the whole point of it: a ball
 * that came to rest against a standing surface would be held back by it for
 * as long as the wheel turned underneath, and would never settle.
 */
export const POCKET_LIP_Y = POCKET_FLOOR_Y + 0.035;
/**
 * The number band: the sloping ring just outside the pockets that carries the
 * numbers on a real wheel — here, the field's own colour showing between the
 * frets. It belongs to the turning head, and it lies exactly on `bowlSurfaceY`
 * so that the head and the bowl are one continuous surface for the ball: the
 * band is where the dish starts, not something perched on top of it.
 */
export const R_BAND_OUT = R_POCKET_OUT + 0.075;
/** How far the dish climbs from the lip out to the ball track… */
export const BOWL_RISE = 0.152;
/** …and how much more the wall at the outer edge adds on top of it. */
export const WALL_RISE = 0.115;
/**
 * The wheel's widest point: the outside of the rim, where the turned crown
 * rolls over and the body starts falling away beneath it. It is also what
 * casts the shadow on the cloth, which is the other reason it has a name.
 */
export const R_BODY = R_RIM + 0.08;
/** Underside of the wheel body, where it meets the felt. */
export const BASE_Y = -0.2;
/** Tip of the cone in the middle of the head. */
export const CONE_TOP_Y = 0.2;
export const TURRET_TOP_Y = 0.31;

export const BALL_RADIUS = 0.06;
/** Where on the wall the ball is thrown in, and where it waits between spins. */
export const BALL_TRACK_RADIUS = 1.34;

/**
 * The avatar plate painted on each pocket floor. It lies flat in the wheel and
 * turns with it, set radially the way a real wheel's numbers are, and sits out
 * towards the wide end of the wedge where there is room for it: the arc across
 * a pocket at this radius is a shade over the plate's own width. Not any
 * further out, though — that is where a settled ball comes to rest, and it
 * would sit on top of the face it is announcing.
 */
export const CHIP_RADIUS = 0.78;
export const CHIP_SIZE = 0.215;
/** A hair above the floor, so it doesn't fight the floor for the same depth. */
export const CHIP_Y = POCKET_FLOOR_Y + 0.004;

/**
 * The diamonds a real ball rattles off, set into the dish at two radii the
 * way a real bowl's are. Not up on the track wall: that is where the ball
 * rides while it is still quick, and it would strike one on the first lap
 * with no ride at all. Nor down against the brass ring, which is where the
 * inner four of them used to sit: a diamond is let into the wood of the
 * apron, with clear wood on both sides of it.
 */
export const DEFLECTOR_COUNT = 8;
export const DEFLECTOR_RADIUS = 1.17;

/**
 * The height of the bowl at radius `r`, from the outer edge of the pockets
 * outwards. It is one surface, not a bowl with an apron bolted on: the dish
 * runs all the way down to the lip of the pocket ring, so a ball that comes
 * back out of a pocket meets a gentle ramp and rolls back in. It used to meet
 * a short, near-vertical step there, which punted it up and inward hard
 * enough to keep it skittering across the wheel for fifteen seconds.
 *
 * Two pieces. Out to `R_TRACK` it is a shallow dish, slightly concave — the
 * exponent just above 1 is what makes it a dish rather than a cone, and it is
 * what gives the ball a radius to sit at for each speed, so it spirals down as
 * it slows instead of holding station and then dropping like a stone. Past
 * that, the wall of the ball track: it leaves the dish at exactly the slope
 * the dish arrives with, so there is no kink for the ball to catch on, and
 * steepens quadratically, so a fast ball finds its own height up the wall and
 * rides there, as one does on a real wheel. The curve is not clamped at the
 * rim: carrying on getting steeper is what keeps even the hardest throw in.
 */
const DISH_RUN = R_TRACK - R_POCKET_OUT;
const DISH_SHAPE = 1.15;
/** The dish's slope where it meets the wall, which the wall carries on from. */
const TRACK_SLOPE = (BOWL_RISE * DISH_SHAPE) / DISH_RUN;
const WALL_RUN = R_RIM - R_TRACK;
const WALL_CURVE = (WALL_RISE - TRACK_SLOPE * WALL_RUN) / (WALL_RUN * WALL_RUN);

export function bowlSurfaceY(r: number): number {
  if (r <= R_TRACK) {
    const s = Math.min(1, Math.max(0, (r - R_POCKET_OUT) / DISH_RUN));
    return POCKET_LIP_Y + BOWL_RISE * Math.pow(s, DISH_SHAPE);
  }
  const w = r - R_TRACK;
  return POCKET_LIP_Y + BOWL_RISE + TRACK_SLOPE * w + WALL_CURVE * w * w;
}

/**
 * Height of the cone at radius `r`. It meets the pocket floor exactly at the
 * ring's inner edge, so a ball that reaches the middle of the wheel is rolled
 * back out into the pockets rather than stopped by a step.
 */
export function coneSurfaceY(r: number): number {
  const u = Math.min(1, Math.max(0, r / R_POCKET_IN));
  return CONE_TOP_Y - (CONE_TOP_Y - POCKET_FLOOR_Y) * Math.pow(u, 1.45);
}

/** Half the angular width of a fret, at the radius it is measured from. */
export const FRET_HALF_WIDTH = 0.0052;
/**
 * How far a fret runs, which is further than the pocket it divides at both
 * ends: inwards it laps onto the foot of the cone, outwards it crosses the
 * number band, the way a real wheel's frets are let into both. Visual only —
 * the ball meets a fret anywhere along the pocket ring either way.
 */
export const FRET_INNER_R = R_POCKET_IN - 0.035;
export const FRET_OUTER_R = R_BAND_OUT;

/**
 * The brass ring the number band runs against, at the very foot of the bowl.
 * It is a band of the bowl's own surface, not something standing on it — a
 * raised ring here would be a ledge the simulated ball knows nothing about.
 */
export const RING_WIDTH = 0.05;

/** The spindle handle across the top of the turret. */
export const HANDLE_ARM = 0.2;
export const HANDLE_Y = TURRET_TOP_Y - 0.045;
export const HANDLE_THICKNESS = 0.016;

export interface Deflector {
  angle: number;
  radius: number;
  y: number;
  /**
   * Laid across the wheel rather than pointing at the middle of it. Every
   * other one is, which is the arrangement a real bowl uses: a diamond turned
   * across the ball's path throws it differently from one turned along it, so
   * alternating them is what stops the eight of them scattering the ball the
   * same way every lap.
   */
  turned: boolean;
}

/**
 * The brass diamonds set into the bowl wall — all on one ring, at one height,
 * turning through ninety degrees from each to the next. Shared, so the ones
 * the ball bounces off are in exactly the places the ones on screen are drawn.
 */
export const DEFLECTOR_SIZE = 0.03;
export const DEFLECTORS: Deflector[] = Array.from({ length: DEFLECTOR_COUNT }, (_, i) => ({
  angle: (i / DEFLECTOR_COUNT) * Math.PI * 2 + Math.PI / DEFLECTOR_COUNT,
  radius: DEFLECTOR_RADIUS,
  y: bowlSurfaceY(DEFLECTOR_RADIUS),
  turned: i % 2 === 1,
}));
